import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  CircularProgress,
  Alert,
  Snackbar,
  Chip,
  TextField
} from '@mui/material';
import { supabase } from '../../services/supabase';
import DeleteIcon from '@mui/icons-material/Delete';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GroupIcon from '@mui/icons-material/Group';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AddToPhotosIcon from '@mui/icons-material/AddToPhotos';

interface SeatingPlan {
  id: string;
  league_id: string;
  session_date: string;
  created_at: string;
  leagueName?: string;
  clubName?: string;
  playerCount?: number;
  gameCount?: number;
  games?: any; // Games from the seating plan
  judgeId?: string; // League judge ID
}

const SeatingsListAdmin: React.FC = () => {
  const [seatingPlans, setSeatingPlans] = useState<SeatingPlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [seatingToDelete, setSeatingToDelete] = useState<string | null>(null);
  const [generateGamesDialogOpen, setGenerateGamesDialogOpen] = useState(false);
  const [seatingToGenerateGames, setSeatingToGenerateGames] = useState<SeatingPlan | null>(null);
  const [sessionNumber, setSessionNumber] = useState<number>(1);
  const [generatingGames, setGeneratingGames] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });

  useEffect(() => {
    fetchSeatingPlans();
  }, []);

  const fetchSeatingPlans = async () => {
    setLoading(true);
    try {
      // Import the helpers we need
      const { fixTypes, getCurrentUserRole, isSuperAdmin } = await import('../../services/adminPermissions');
      
      // Check if user is a superadmin
      const superAdmin = await isSuperAdmin();
      let seatingQueryResult;
      
      // Start with base query for seating plans
      let seatingBaseQuery = supabase
        .from('seating_plans')
        .select(`
          id,
          league_id,
          session_date,
          created_at,
          games
        `);
      
      if (superAdmin) {
        // Superadmins see all seating plans
        seatingQueryResult = await seatingBaseQuery.order('created_at', { ascending: false });
      } else {
        // Regular admins need filtering based on their club
        const userRole = await getCurrentUserRole();
        
        if (!userRole?.club_id) {
          // No club assigned, return empty result
          seatingQueryResult = { data: [], error: null };
        } else {
          // Get leagues in the admin's club first
          const { data: leagueIds } = await supabase
            .from('leagues')
            .select('id')
            .eq('club_id', userRole.club_id);
            
          if (!leagueIds || leagueIds.length === 0) {
            // No leagues in this club, return empty result
            seatingQueryResult = { data: [], error: null };
          } else {
            // Filter seating plans by these league IDs
            const leagueIdList = leagueIds.map(league => league.id);
            seatingQueryResult = await seatingBaseQuery
              .in('league_id', leagueIdList)
              .order('created_at', { ascending: false });
          }
        }
      }
      
      const { data: seatingData, error: seatingError } = seatingQueryResult;
      if (seatingError) throw seatingError;

      // Set up the leagues query now
      let leaguesQueryResult;
      
      // Start with base leagues query
      let leaguesBaseQuery = supabase
        .from('leagues')
        .select(`
          id, 
          name,
          club:club_id (
            id,
            name
          ),
          judge_id
        `);
      
      if (superAdmin) {
        // Superadmins see all leagues
        leaguesQueryResult = await leaguesBaseQuery;
      } else {
        // Regular admins only see leagues from their club
        const userRole = await getCurrentUserRole();
        
        if (!userRole?.club_id) {
          // No club assigned, return empty result
          leaguesQueryResult = { data: [], error: null };
        } else {
          // Filter leagues by the admin's club
          leaguesQueryResult = await leaguesBaseQuery.eq('club_id', userRole.club_id);
        }
      }
      
      const { data: leaguesData, error: leaguesError } = leaguesQueryResult;
      if (leaguesError) throw leaguesError;

      // Create a map of leagues
      const leaguesMap = new Map();
      leaguesData.forEach((league: any) => {
        leaguesMap.set(league.id, {
          name: league.name,
          clubName: league.club?.name || 'Unknown Club',
          judgeId: league.judge_id
        });
      });

      // Enrich seating data with league names
      const enrichedSeatingData = seatingData.map((seating: any) => {
        const leagueInfo = leaguesMap.get(seating.league_id) || { 
          name: 'Unknown League', 
          clubName: 'Unknown Club',
          judgeId: null
        };
        
        // Count unique players in all games
        const playerSet = new Set();
        const games = seating.games;
        games.forEach((game: any) => {
          game.seats.forEach((seat: any) => {
            playerSet.add(seat.player_id);
          });
        });

        return {
          ...seating,
          leagueName: leagueInfo.name,
          clubName: leagueInfo.clubName,
          judgeId: leagueInfo.judgeId,
          playerCount: playerSet.size,
          gameCount: games.length,
          games: games
        };
      });

      setSeatingPlans(enrichedSeatingData);
    } catch (error: any) {
      console.error('Error fetching seating plans:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (id: string) => {
    setSeatingToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!seatingToDelete) return;
    
    try {
      const { error } = await supabase
        .from('seating_plans')
        .delete()
        .eq('id', seatingToDelete);
      
      if (error) throw error;
      
      // Remove the deleted item from the state
      setSeatingPlans(seatingPlans.filter(plan => plan.id !== seatingToDelete));
      showSnackbar('Seating plan deleted successfully', 'success');
    } catch (error: any) {
      console.error('Error deleting seating plan:', error);
      showSnackbar(`Failed to delete: ${error.message}`, 'error');
    } finally {
      setDeleteDialogOpen(false);
      setSeatingToDelete(null);
    }
  };

  const copyToClipboard = (id: string) => {
    const url = `${window.location.origin}/seating-plan/${id}`;
    navigator.clipboard.writeText(url)
      .then(() => showSnackbar('Link copied to clipboard', 'info'))
      .catch(() => showSnackbar('Failed to copy link', 'error'));
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };
  
  const handleGenerateGames = (seatingPlan: SeatingPlan) => {
    setSeatingToGenerateGames(seatingPlan);
    setGenerateGamesDialogOpen(true);
  };
  
  const confirmGenerateGames = async () => {
    if (!seatingToGenerateGames) return;
    
    setGeneratingGames(true);
    
    try {
      const seatingPlan = seatingToGenerateGames;
      const games = seatingPlan.games;
      const sessionDate = new Date(seatingPlan.session_date);
      const sessionNum = sessionNumber;
      const leagueId = seatingPlan.league_id;
      const judgeId = seatingPlan.judgeId;
      
      if (!judgeId) {
        throw new Error('Judge ID not found for this league');
      }
      
      // Create each game and its results
      const createdGames = [];
      
      for (const game of games) {
        const gameNumber = game.game_number;
        
        // Calculate game time (adding 1 hour for each game after the first)
        const gameTime = new Date(sessionDate);
        gameTime.setHours(gameTime.getHours() + (gameNumber - 1));
        
        // 1. Create the game
        const { data: gameData, error: gameError } = await supabase
          .from('games')
          .insert({
            session_num: sessionNum,
            number_in_session: gameNumber,
            league_id: leagueId,
            occurred_at: gameTime.toISOString(),
            winner: 'CIVILIANS', // Default winner is civilians
            judge_id: judgeId
          })
          .select()
          .single();
          
        if (gameError) throw gameError;
        
        createdGames.push(gameData);
        
        // 2. Create game results for each seat
        const gameResults = game.seats.map((seat: any) => ({
          game_id: gameData.id,
          player_id: seat.player_id,
          player_num: seat.seat_number,
          is_win: true, // Default everyone to winning (civilians win by default)
          bonus_points: 0,
          role: 'CITIZEN', // Default role for everyone
          is_killed_first: false
        }));
        
        const { error: resultsError } = await supabase
          .from('game_results')
          .insert(gameResults);
          
        if (resultsError) throw resultsError;
      }
      
      showSnackbar(`Successfully created ${createdGames.length} games for session ${sessionNum}`, 'success');
    } catch (error: any) {
      console.error('Error generating games:', error);
      showSnackbar(`Failed to generate games: ${error.message}`, 'error');
    } finally {
      setGeneratingGames(false);
      setGenerateGamesDialogOpen(false);
      setSeatingToGenerateGames(null);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 3 }}>
        Error loading seating plans: {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
        Saved Seating Plans
      </Typography>

      {seatingPlans.length === 0 ? (
        <Alert severity="info">No seating plans found. Create your first seating plan to see it here.</Alert>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="medium">
            <TableHead>
              <TableRow>
                <TableCell><Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Date</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>League</Typography></TableCell>
                <TableCell><Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Info</Typography></TableCell>
                <TableCell align="right"><Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>Actions</Typography></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {seatingPlans.map((plan) => (
                <TableRow 
                  key={plan.id} 
                  hover
                  onClick={() => window.location.href = `/seating-plan/${plan.id}`}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarTodayIcon sx={{ mr: 1, fontSize: '0.9rem', color: 'primary.main' }} />
                      {formatDate(plan.session_date)}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <EmojiEventsIcon sx={{ mr: 1, fontSize: '0.9rem', color: 'primary.main' }} />
                      {plan.leagueName}
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {plan.clubName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip 
                        size="small" 
                        icon={<GroupIcon />} 
                        label={`${plan.playerCount} players`} 
                        variant="outlined"
                      />
                      <Chip 
                        size="small" 
                        label={`${plan.gameCount} games`} 
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Generate Games">
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGenerateGames(plan);
                        }}
                        size="small"
                        color="success"
                      >
                        <AddToPhotosIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copy link">
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          copyToClipboard(plan.id);
                        }}
                        size="small"
                        color="primary"
                      >
                        <ContentCopyIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete seating plan">
                      <IconButton 
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(plan.id);
                        }}
                        size="small"
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this seating plan? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Generate Games dialog */}
      <Dialog
        open={generateGamesDialogOpen}
        onClose={() => !generatingGames && setGenerateGamesDialogOpen(false)}
      >
        <DialogTitle>Generate Games from Seating Plan</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            This will create {seatingToGenerateGames?.gameCount || 0} games in the {seatingToGenerateGames?.leagueName} league.
            Please select the session number
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            id="session-number"
            label="Session Number"
            type="number"
            fullWidth
            variant="outlined"
            value={sessionNumber}
            onChange={(e) => setSessionNumber(parseInt(e.target.value) || 1)}
            inputProps={{ min: 1 }}
            disabled={generatingGames}
          />
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setGenerateGamesDialogOpen(false)}
            disabled={generatingGames}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmGenerateGames} 
            color="primary" 
            variant="contained"
            disabled={generatingGames}
            startIcon={generatingGames ? <CircularProgress size={20} /> : undefined}
          >
            {generatingGames ? 'Generating...' : 'Generate Games'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SeatingsListAdmin;